import { useNavigate } from 'react-router-dom'
import { GUIA_LABELS } from '@utils/guiaLabels.js'
import { ROUTES, topicoDetailPath } from '@utils/constants.js'
import { EmptyPanel } from '@components/primitives/EmptyPanel.js'
import { formatPassoOrdem } from '@utils/passoLabel.js'
import { barFillStyle } from '@utils/barStyle.js'
import type { ModuloChecklistPendente } from '@utils/checklistPendentes.js'

interface Props {
  modulos: ModuloChecklistPendente[]
  totalItens: number
  onToggleItem: (topicoId: string, moduloId: string, itemId: string) => void
  compact?: boolean
}

function EmptyState() {
  const navigate = useNavigate()

  return (
    <EmptyPanel
      title='Nenhum item do guia atrasado'
      description='Checklists das etapas que você já passou estão concluídos.'
      actionLabel='Continuar na jornada'
      onAction={() => navigate(ROUTES.MODULOS)}
    />
  )
}

export function ChecklistPendentesView({ modulos, totalItens, onToggleItem, compact }: Props) {
  const navigate = useNavigate()

  if (modulos.length === 0) {
    return <EmptyState />
  }

  return (
    <div className='cf3-checklist-pendente'>
      {!compact && (
        <p className='cf3-checklist-pendente__summary'>
          {totalItens} {totalItens === 1 ? 'item pendente' : 'itens pendentes'} em etapas que você
          já avançou na jornada.
        </p>
      )}

      {modulos.map((mod) => (
        <section key={mod.moduloId} className='cf3-checklist-pendente__modulo'>
          <header className='cf3-checklist-pendente__modulo-head'>
            <span className='cf3-checklist-pendente__modulo-icon' aria-hidden>
              {mod.moduloIcone}
            </span>
            <span className='cf3-checklist-pendente__modulo-title'>{mod.moduloTitulo}</span>
            <span className='cf3-checklist-pendente__modulo-count'>{mod.totalPendentes}</span>
          </header>

          {mod.passos.map(({ passo, itensPendentes, total, concluidos, percentual }) => (
            <article key={passo.topicoId} className='cf3-checklist-pendente__passo'>
              <div className='cf3-checklist-pendente__passo-head'>
                <div className='cf3-checklist-pendente__passo-info'>
                  <h3 className='cf3-checklist-pendente__passo-title'>{passo.titulo}</h3>
                  <p className='cf3-checklist-pendente__passo-meta'>
                    {formatPassoOrdem(passo)} · {concluidos}/{total} concluídos
                  </p>
                </div>
                <button
                  type='button'
                  className='cf3-checklist-pendente__passo-link'
                  onClick={() => navigate(topicoDetailPath(passo.moduloId, passo.topicoId))}
                >
                  {GUIA_LABELS.abrirEtapa}
                </button>
              </div>

              <div
                className='cf3-checklist-bar cf3-checklist-pendente__bar'
                role='progressbar'
                aria-valuenow={percentual}
                aria-valuemin={0}
                aria-valuemax={100}
              >
                <div className='cf3-checklist-bar__fill' style={barFillStyle(percentual)} />
              </div>

              <ul className='cf3-checklist-list'>
                {itensPendentes.map((item) => (
                  <li key={item.id} className='cf3-checklist-item'>
                    <label className='cf3-checklist-item__label'>
                      <input
                        type='checkbox'
                        checked={false}
                        onChange={() => onToggleItem(passo.topicoId, passo.moduloId, item.id)}
                      />
                      <span className='cf3-checklist-item__texto'>{item.texto}</span>
                    </label>
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </section>
      ))}
    </div>
  )
}
